import { safeNum } from "./safemath";

const ALLOWED_VIDEO_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtu.be",
]);

export function calculateReward(views, rewardPerView) {
  const v = safeNum(views);
  const r = safeNum(rewardPerView);
  if (v <= 0 || r <= 0) return 0;
  return v * r;
}

export function isEligibleForSettlement(views, rewardPerView, config = {}) {
  const v = safeNum(views);
  if (v <= 0) return false;
  const minViews = safeNum(config.minViews);
  const minAmount = safeNum(config.minAmount);
  const amount = calculateReward(v, rewardPerView);
  if (minViews > 0 && v < minViews) return false;
  if (minAmount > 0 && amount < minAmount) return false;
  return true;
}

export function sumViews(rows, key = "latest_view_count") {
  if (!Array.isArray(rows)) return 0;
  return rows.reduce((acc, row) => acc + safeNum(row?.[key]), 0);
}

// 메인(role='required') 영상 중 유효한 것만. 잘못된 영상(is_valid===false)은 제외.
// RPC(confirm_promotion_settlement / get_promotion_progress) 와 동일한 기준 →
// 클라이언트 표시·예상금액 계산이 서버 정산과 일치하도록 보장.
// 참고: 서버 검증 전 "검증 대기"(is_valid=false, invalid_reason=null) 영상도 여기서 제외된다.
// 정산은 검증(is_valid=true)된 뒤에만 산입되므로 서버와 일치(fail-closed).
export function validRequiredVideos(videos) {
  if (!Array.isArray(videos)) return [];
  return videos.filter((v) => v?.role === "required" && v?.is_valid !== false);
}

// 영상 유효성 표시 상태:
//   'valid'   — 서버 검증 통과(is_valid=true) 또는 레거시(is_valid 미설정, 백필 전 호환)
//   'pending' — 서버 검증 대기(is_valid=false, invalid_reason 없음). 정산 제외, 트래커/새로고침 후 확정
//   'invalid' — 서버 검증 결과 부적합(is_valid=false, invalid_reason 존재). 정산 제외
// 마이그레이션 20260709000010 이후 사용자 제출 영상은 검증 전까지 'pending' 으로 시작한다.
export function videoValidityStatus(v) {
  if (!v) return "pending";
  if (v.is_valid === false) return v.invalid_reason ? "invalid" : "pending";
  return "valid";
}

// 영상 개수 기반(videos 템플릿) 정산 단가: 유효 영상 개수 × 영상당 리워드.
export function calculateVideoReward(count, rewardPerVideo) {
  const c = safeNum(count);
  const r = safeNum(rewardPerVideo);
  if (c <= 0 || r <= 0) return 0;
  return c * r;
}

export function parseYoutubeVideoId(url) {
  if (!url || typeof url !== "string") return null;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  if (host === "youtu.be") {
    const id = parsed.pathname.replace(/^\/+/, "").split("/")[0];
    return id || null;
  }
  if (
    host === "youtube.com" ||
    host === "www.youtube.com" ||
    host === "m.youtube.com"
  ) {
    const shortsMatch = parsed.pathname.match(/^\/shorts\/([^/?#]+)/);
    if (shortsMatch) return shortsMatch[1];
    const v = parsed.searchParams.get("v");
    if (v) return v;
    const embedMatch = parsed.pathname.match(/^\/embed\/([^/?#]+)/);
    if (embedMatch) return embedMatch[1];
  }
  return null;
}

export function isAllowedVideoUrl(url) {
  if (!url || typeof url !== "string") return false;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
  return ALLOWED_VIDEO_HOSTS.has(parsed.hostname.toLowerCase());
}

// 한 promotion_settlement 행의 reward_amount 를 active submission 채널별로 분배.
// 공식은 RPC `get_user_promotion_settlement_summary` 와 일치 —
// role='required' 영상의 latest_view_count 합 비율로 분배, is_valid 미필터(RPC 동작 그대로).
// 조회수 합이 0이면(예: videos 템플릿 프로모션 — 영상 개수 × 단가 정산, 조회수 미수집)
// 유효 메인 영상 개수(role='required', is_valid!==false) 비율로 폴백 분배 (20260724000001 RPC 와 동일).
// 같은 application 안에 같은 channel_id 의 submission 이 여러 개면 RPC 처럼 채널 단위로 합산.
// 조회수·유효 영상 모두 0 / 활성 submission 없음 → 빈 배열 (회원 합계엔 살아있음, 채널 분배 안함).
// 입력 형태: { reward_amount, application: { submissions: [{ channel_id, channel_name, status, videos: [{ role, latest_view_count, is_valid }] }] } }
export function splitPromotionSettlementByChannel(settlement) {
  if (!settlement || !settlement.application) return [];
  const submissions = Array.isArray(settlement.application.submissions)
    ? settlement.application.submissions
    : [];
  const activeSubs = submissions.filter((s) => s?.status === "active");
  if (activeSubs.length === 0) return [];

  const grouped = {};
  activeSubs.forEach((s) => {
    const channelIdYoutube = s?.channel_id || "";
    const channelName = s?.channel_name || "";
    const key = `${channelIdYoutube}::${channelName}`;
    if (!grouped[key]) {
      grouped[key] = {
        channelIdYoutube,
        channelName,
        requiredViews: 0,
        requiredValidVideos: 0,
      };
    }
    const videos = Array.isArray(s?.videos) ? s.videos : [];
    const requiredVideos = videos.filter((v) => v?.role === "required");
    grouped[key].requiredViews += requiredVideos.reduce(
      (acc, v) => acc + safeNum(v?.latest_view_count),
      0,
    );
    grouped[key].requiredValidVideos += requiredVideos.filter(
      (v) => v?.is_valid !== false,
    ).length;
  });

  const perChannel = Object.values(grouped);
  const totalViews = perChannel.reduce((acc, c) => acc + c.requiredViews, 0);
  const useViews = totalViews > 0;
  const weightOf = (c) => (useViews ? c.requiredViews : c.requiredValidVideos);
  const totalWeight = useViews
    ? totalViews
    : perChannel.reduce((acc, c) => acc + c.requiredValidVideos, 0);
  if (totalWeight <= 0) return [];

  const rewardAmount = safeNum(settlement.reward_amount);
  return perChannel
    .filter((c) => weightOf(c) > 0)
    .map((c) => ({
      ...c,
      amount: (rewardAmount * weightOf(c)) / totalWeight,
    }));
}
